import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { CartItem, DeliveryRule, StoreInterface } from "../../../types/types";
import { DeliveryType } from "../../../types/enums";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectStoreList } from "../../../../store/slices/storeListSlice";

interface UseDeliveryCalculatorProps {
  cartItems: CartItem[];
  deliveryAddress?: {
    latitude: number;
    longitude: number;
  } | null;
  deliveryType: DeliveryType;
}

interface StoreWithDistance extends StoreInterface {
  distance: number;
}

export const useDeliveryCalculator = ({
  cartItems,
  deliveryAddress,
  deliveryType,
}: UseDeliveryCalculatorProps) => {
  const currentCluster = useSelector(selectCurrentCluster);
  const allStores = useSelector(selectStoreList);

  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(false);
  const [deliveryStoreId, setDeliveryStoreId] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const cartItemsKey = useMemo(() => {
    return cartItems.map((item) => `${item.id}:${item.quantity}`).join("|");
  }, [cartItems]);

  useEffect(() => {
    const calculateDelivery = async () => {
      console.log("[Delivery] ▶ Старт расчёта", {
        deliveryType,
        deliveryAddress,
        hasCluster: !!currentCluster,
        storesCount: allStores.length,
        cartItemsKey,
      });

      setAvailable(false);
      setDeliveryStoreId(null);
      setDeliveryDistance(null);
      setDeliveryFee(0);
      setError(null);

      if (
        deliveryType !== DeliveryType.DELIVERY ||
        !deliveryAddress ||
        !currentCluster
      ) {
        console.log("[Delivery] ⏭ Пропуск: условия не выполнены", {
          isDelivery: deliveryType === DeliveryType.DELIVERY,
          hasAddress: !!deliveryAddress,
          hasCluster: !!currentCluster,
        });
        return;
      }

      setLoading(true);

      try {
        const storesWithDelivery = allStores.filter((s) => s.withDelivery);
        console.log(
          `[Delivery] 🏬 Магазинов с доставкой: ${storesWithDelivery.length} из ${allStores.length}`,
        );

        if (!storesWithDelivery.length) {
          console.warn("[Delivery] ❌ Нет точек с доставкой");
          setError("Нет точек с доставкой");
          return;
        }

        const rulesByRadius = new Map<number, DeliveryRule[]>();
        currentCluster.delivery?.forEach((rule: DeliveryRule) => {
          if (!rulesByRadius.has(rule.radius)) {
            rulesByRadius.set(rule.radius, []);
          }
          rulesByRadius.get(rule.radius)!.push(rule);
        });

        const sortedRadii = [...rulesByRadius.keys()].sort((a, b) => a - b);
        console.log("[Delivery] 📏 Радиусы правил (м):", sortedRadii);

        let nearestStore: StoreWithDistance | null = null;
        let minDistance = Infinity;

        for (const store of storesWithDelivery) {
          if (!store.address?.latitude || !store.address?.longitude) {
            console.log(`[Delivery] ⚠ Магазин ${store.id}: нет координат`);
            continue;
          }

          const distance = calculateDistance(
            Number(store.address.latitude),
            Number(store.address.longitude),
            Number(deliveryAddress.latitude),
            Number(deliveryAddress.longitude),
          );

          if (isNaN(distance)) {
            console.log(`[Delivery] ⚠ Магазин ${store.id}: distance = NaN`);
            continue;
          }

          const distanceMeters = distance * 1000;
          const withinStoreRadius =
            store.deliveryRadius === null ||
            distanceMeters <= store.deliveryRadius;

          console.log(
            `[Delivery] 📍 Магазин ${store.id}: ${distance.toFixed(3)} км (${distanceMeters.toFixed(0)} м), ` +
              `радиус магазина: ${store.deliveryRadius ?? "∞"}, входит: ${withinStoreRadius}`,
          );

          if (!withinStoreRadius) continue;

          if (distance < minDistance) {
            minDistance = distance;
            nearestStore = { ...store, distance };
          }
        }

        if (!nearestStore) {
          console.warn("[Delivery] ❌ Нет магазина в радиусе доставки");
          setError("Доставка недоступна по этому адресу");
          return;
        }

        console.log(
          `[Delivery] ✅ Ближайший магазин: ${nearestStore.id}, ${nearestStore.distance.toFixed(3)} км`,
        );

        const itemsTotal = cartItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );
        console.log(`[Delivery] 💰 Сумма корзины: ${itemsTotal}`);

        let targetRadius: number | undefined;
        const store = nearestStore;
        targetRadius = sortedRadii.find((r) => r >= store.distance * 1000);

        if (!targetRadius && sortedRadii.length) {
          targetRadius = sortedRadii[sortedRadii.length - 1];
          console.log(
            `[Delivery] ⚠ Радиус не найден, берём максимальный: ${targetRadius}`,
          );
        }

        console.log(`[Delivery] 🎯 Целевой радиус: ${targetRadius ?? "нет"}`);

        let price = 0;
        if (targetRadius) {
          const radiusRules = rulesByRadius.get(targetRadius) || [];
          const sortedRules = [...radiusRules].sort(
            (a, b) => a.level - b.level,
          );
          console.log(
            `[Delivery] 📋 Правила для радиуса ${targetRadius}:`,
            sortedRules.map((r) => ({ level: r.level, value: r.value })),
          );

          for (let i = sortedRules.length - 1; i >= 0; i--) {
            if (sortedRules[i].level <= itemsTotal) {
              price = sortedRules[i].value;
              console.log(
                `[Delivery] 💵 Применено правило: level=${sortedRules[i].level}, value=${price}`,
              );
              break;
            }
          }

          if (price === 0) {
            console.log("[Delivery] 💵 Ни одно правило не подошло, цена = 0");
          }
        }

        setAvailable(true);
        setDeliveryStoreId(store.id ?? null);
        setDeliveryDistance(store.distance);
        setDeliveryFee(price);

        console.log("[Delivery] 🏁 Итог:", {
          available: true,
          storeId: store.id,
          distanceKm: store.distance,
          fee: price,
        });
      } catch (err) {
        console.error("[Delivery] 💥 Ошибка расчёта:", err);
        setError("Ошибка расчета доставки");
      } finally {
        setLoading(false);
        console.log("[Delivery] ◀ Завершено");
      }
    };

    calculateDelivery();
  }, [deliveryType, deliveryAddress, currentCluster, allStores, cartItemsKey]);

  return {
    available,
    deliveryStoreId,
    deliveryDistance,
    deliveryFee,
    loading,
    error,
  };
};

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};